import { ref } from 'vue';

/**
 * Composable for calculating alignment guides on a canvas
 * @param {Number} canvasWidth - Width of the canvas in pixels (default: 1080)
 * @param {Number} canvasHeight - Height of the canvas in pixels (default: 1920)
 * @param {Number} detectionTolerance - Tolerance for detecting nearby guides (default: 20)
 * @returns {Object} Alignment guides state and methods
 */
export function useAlignmentGuides(
  canvasWidth = 1080,
  canvasHeight = 1920,
  detectionTolerance = 20
) {
  const alignmentGuides = ref({ vertical: [], horizontal: [] });

  /**
   * Calculate alignment guides for a block being dragged
   * @param {Array} allBlocks - All blocks on the canvas
   * @param {Number} currentBlockIdx - Index of the block being dragged
   * @param {Number} x - X position of the block
   * @param {Number} y - Y position of the block
   * @param {Number} width - Width of the block
   * @param {Number} height - Height of the block
   * @returns {Object} Vertical and horizontal guides
   */
  const calculateAlignmentGuides = (allBlocks, currentBlockIdx, x, y, width, height) => {
    const guides = { vertical: [], horizontal: [] };

    // Calculate block edges and center
    const left = x;
    const right = x + width;
    const centerX = x + width / 2;
    const top = y;
    const bottom = y + height;
    const centerY = y + height / 2;

    // Add canvas center guides (purple guides)
    guides.vertical.push({ value: canvasWidth / 2, type: 'canvas', align: 'center' });
    guides.horizontal.push({ value: canvasHeight / 2, type: 'canvas', align: 'center' });

    // Check alignment with other blocks (blue guides)
    allBlocks.forEach((block, bIdx) => {
      // Skip the current block being dragged
      if (bIdx === currentBlockIdx) return;

      const bLeft = block.x;
      const bRight = block.x + block.width;
      const bCenterX = block.x + block.width / 2;
      const bTop = block.y;
      const bBottom = block.y + block.height;
      const bCenterY = block.y + block.height / 2;

      // Check vertical alignment (left, right, center edges)
      [
        { value: bLeft, align: 'left' },
        { value: bRight, align: 'right' },
        { value: bCenterX, align: 'center' }
      ].forEach(guide => {
        // Check if any edge of the current block is close to this guide
        if (
          Math.abs(guide.value - left) < detectionTolerance ||
          Math.abs(guide.value - right) < detectionTolerance ||
          Math.abs(guide.value - centerX) < detectionTolerance
        ) {
          guides.vertical.push({ value: guide.value, type: 'block', align: guide.align });
        }
      });

      // Check horizontal alignment (top, bottom, center edges)
      [
        { value: bTop, align: 'top' },
        { value: bBottom, align: 'bottom' },
        { value: bCenterY, align: 'center' }
      ].forEach(guide => {
        // Check if any edge of the current block is close to this guide
        if (
          Math.abs(guide.value - top) < detectionTolerance ||
          Math.abs(guide.value - bottom) < detectionTolerance ||
          Math.abs(guide.value - centerY) < detectionTolerance
        ) {
          guides.horizontal.push({ value: guide.value, type: 'block', align: guide.align });
        }
      });
    });

    return guides;
  };

  /**
   * Clear all alignment guides
   */
  const clearGuides = () => {
    alignmentGuides.value = { vertical: [], horizontal: [] };
  };

  return {
    // State
    alignmentGuides,

    // Methods
    calculateAlignmentGuides,
    clearGuides
  };
}
